// Components/CompanyForm.tsx

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  AdvancedSearchMetadataCountry,
  AdvancedSearchMetadataIndustry,
  CompanyFormData,
} from '../../../../Types/AdvanceSearchResultsTypes';
import { actions } from '../../../../Slice/AdvanceSearchSlice';
import {
  selectAdvanceSearchMetadata,
  selectAdvanceSearchCompanyForm,
} from '../../../../Selector/AdvanceSearchSelector';
import { FormField } from './FormField';

const FormContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

interface CompanyFormProps {
  onValidityChange?: (isValid: boolean) => void;
  onFormDataChange?: (data: CompanyFormData) => void;
  isLoading?: boolean;
}

export const CompanyForm: React.FC<CompanyFormProps> = ({ onValidityChange, onFormDataChange, isLoading }) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectAdvanceSearchMetadata);
  const formData = useSelector(selectAdvanceSearchCompanyForm);
  const [industryId, setIndustryId] = useState(0);
  const [revenueId, setRevenueId] = useState(0);
  const [countryId, setCountryId] = useState(0);
  const [regionId, setRegionId] = useState(0);
  const [sizeId, setSizeId] = useState(0);
  const industryOptions: { id: number; label: string }[] = (filters?.industries ?? [])
    .map((ind: AdvancedSearchMetadataIndustry) => ({ id: ind.id, label: ind.name }));
  const revenueOptions: { id: number; label: string }[] = (filters?.companyRevenue ?? [])
    .map((label: string, i: number) => ({ id: i + 1, label }));
  const countryOptions: { id: number; label: string }[] = (filters?.countries ?? [])
    .map((c: AdvancedSearchMetadataCountry) => ({ id: c.id, label: c.country_name }));
  const regionOptions: { id: number; label: string }[] = (filters?.regions ?? [])
    .map((label: string, i: number) => ({ id: i + 1, label }));
  const companySizeOptions: { id: number; label: string }[] = (filters?.companySizes ?? [])
    .map((label: string, i: number) => ({ id: i + 1, label }));

  const handleIndustryChange = (id: number) => {
    setIndustryId(id);
    const selectedLabel = industryOptions.find((option) => option.id === id)?.label ?? '';
    updateField('industry', selectedLabel);
  };

  const handleRevenueChange = (id: number) => {
    setRevenueId(id);
    const selectedLabel = revenueOptions.find((option) => option.id === id)?.label ?? '';
    updateField('revenue', selectedLabel);
  };

  const handleCountryChange = (id: number) => {
    setCountryId(id);
    const selectedLabel = countryOptions.find((option) => option.id === id)?.label ?? '';
    updateField('country', selectedLabel);
  };

  const handleRegionChange = (id: number) => {
    setRegionId(id);
    const selectedLabel = regionOptions.find((option) => option.id === id)?.label ?? '';
    updateField('region', selectedLabel);
  };

  const handleSizeChange = (id: number) => {
    setSizeId(id);
    const selectedLabel = companySizeOptions.find((option) => option.id === id)?.label ?? '';
    updateField('size', selectedLabel);
  };

  const updateField = (field: keyof CompanyFormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    dispatch(actions.setAdvanceSearchCompanyForm(newData));
  };

  useEffect(() => {
    const isValid = Object.values(formData).some((val) => {
      if (typeof val === 'number') {
        return val !== 0;
      }
      return String(val).trim() !== '';
    });
    onValidityChange?.(isValid);
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange, onValidityChange]);

  useEffect(() => {
    const matchedId = companySizeOptions.find((option) => option.label === formData.size)?.id ?? 0;
    setSizeId(matchedId);
  }, [companySizeOptions, formData.size]);

  useEffect(() => {
    const matchedId = industryOptions.find((option) => option.label === formData.industry)?.id ?? 0;
    setIndustryId(matchedId);
  }, [formData.industry, industryOptions]);

  useEffect(() => {
    const matchedId = revenueOptions.find((option) => option.label === formData.revenue)?.id ?? 0;
    setRevenueId(matchedId);
  }, [formData.revenue, revenueOptions]);

  useEffect(() => {
    const matchedId = countryOptions.find((option) => option.label === formData.country)?.id ?? 0;
    setCountryId(matchedId);
  }, [countryOptions, formData.country]);

  useEffect(() => {
    const matchedId = regionOptions.find((option) => option.label === formData.region)?.id ?? 0;
    setRegionId(matchedId);
  }, [formData.region, regionOptions]);

  return (
    <FormContainer>
      <FormField
        label="Name"
        value={formData.name}
        onChange={(val) => updateField('name', val)}
        placeholder="Enter company name"
      />
      <FormField
        label="Industry"
        type="dropdown"
        value={industryId}
        onChange={(val) => handleIndustryChange(val as number)}
        options={industryOptions}
        placeholder="Select industry"
        searchPlaceholder="Search industry"
        hasSearch={true}
        isLoading={isLoading}
      />
      <FormField
        label="Revenue"
        type="dropdown"
        value={revenueId}
        onChange={(val) => handleRevenueChange(val as number)}
        options={revenueOptions}
        placeholder="Select revenue range"
        isLoading={isLoading}
      />
      <FormField
        label="Country"
        type="dropdown"
        value={countryId}
        onChange={(val) => handleCountryChange(val as number)}
        options={countryOptions}
        placeholder="Select country"
        hasSearch={true}
        isLoading={isLoading}
      />
      <FormField
        label="Region"
        type="dropdown"
        value={regionId}
        onChange={(val) => handleRegionChange(val as number)}
        options={regionOptions}
        placeholder="Select region"
        isLoading={isLoading}
      />
      <FormField
        label="Size"
        type="dropdown"
        value={sizeId}
        onChange={(val) => handleSizeChange(val as number)}
        options={companySizeOptions}
        placeholder="Select company size"
        isLoading={isLoading}
      />
    </FormContainer>
  );
};